import React from 'react';
import { Mail, Phone, CheckCircle, Package, Factory, Shield, Truck, ClipboardCheck, Globe, Handshake, Search, FileCheck, BarChart3, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';

const Home = () => {
  const { language, toggleLanguage } = useLanguage();
  const t = translations[language];

  const services = [
    {
      icon: Search,
      title: t.services.service1Title,
      description: t.services.service1Desc
    },
    {
      icon: Handshake,
      title: t.services.service2Title,
      description: t.services.service2Desc
    },
    {
      icon: FileCheck,
      title: t.services.service3Title,
      description: t.services.service3Desc
    },
    {
      icon: BarChart3,
      title: t.services.service4Title,
      description: t.services.service4Desc
    },
    {
      icon: Truck,
      title: t.services.service5Title,
      description: t.services.service5Desc
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: t.process.step1Title,
      description: t.process.step1Desc
    },
    {
      number: '02',
      title: t.process.step2Title,
      description: t.process.step2Desc
    },
    {
      number: '03',
      title: t.process.step3Title,
      description: t.process.step3Desc
    },
    {
      number: '04',
      title: t.process.step4Title,
      description: t.process.step4Desc
    },
    {
      number: '05',
      title: t.process.step5Title,
      description: t.process.step5Desc
    }
  ];

  const benefits = [
    {
      icon: CheckCircle,
      title: t.benefits.benefit1Title,
      description: t.benefits.benefit1Desc
    },
    {
      icon: Shield,
      title: t.benefits.benefit2Title,
      description: t.benefits.benefit2Desc
    },
    {
      icon: Factory,
      title: t.benefits.benefit3Title,
      description: t.benefits.benefit3Desc
    },
    {
      icon: Package,
      title: t.benefits.benefit4Title,
      description: t.benefits.benefit4Desc
    }
  ];

  const handleEmail = () => {
    window.location.href = 'mailto:info@nexumtrades.com';
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/351927513090', '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header data-testid="site-header" className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_2edf651e-d225-4f0a-891f-784c5e401242/artifacts/3fwrga1y_logo%20navy-Photoroom.png" 
              alt="NexumTrades Logo" 
              data-testid="header-logo"
              className="h-[4.5rem] md:h-[5.8rem]"
            />
          </div>
          <nav className="hidden md:flex gap-8 text-sm font-medium text-gray-700">
            <a href="#about" className="hover:text-[#1A3260] transition-colors">{t.nav.about}</a>
            <a href="#services" className="hover:text-[#1A3260] transition-colors">{t.nav.services}</a>
            <a href="#process" className="hover:text-[#1A3260] transition-colors">{t.nav.process}</a>
            <a href="#contact" className="hover:text-[#1A3260] transition-colors">{t.nav.contact}</a>
          </nav>
          <div className="flex items-center gap-3">
            <Button
              onClick={toggleLanguage}
              variant="outline"
              size="sm"
              data-testid="language-toggle"
              className="border-gray-300 hover:bg-gray-50"
            >
              <Globe className="h-4 w-4 mr-2" />
              {language === 'en' ? 'PT' : 'EN'}
            </Button>
            <Button 
              onClick={handleEmail}
              data-testid="header-contact-btn"
              className="bg-[#1A3260] hover:bg-[#142848] text-white"
            >
              {t.nav.contactUs}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section - Full Width with Background Image & Overlay */}
      <section data-testid="hero-section" className="relative min-h-[600px] md:min-h-[700px] flex items-center justify-center">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: 'url(https://images.pexels.com/photos/35138560/pexels-photo-35138560.jpeg?auto=compress&cs=tinysrgb&w=1920)' }}
        />
        {/* Dark Navy Overlay */}
        <div className="absolute inset-0 bg-[#1A3260]/50" />
        
        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center pt-24 pb-16">
          <h1 data-testid="hero-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            {t.hero.title}
          </h1>
          <p data-testid="hero-subtitle" className="text-base md:text-lg text-white/90 mb-4 leading-relaxed max-w-2xl mx-auto">
            {t.hero.subtitle}
          </p>
          <p className="text-lg md:text-xl text-white font-semibold mb-10">
            {t.hero.tagline}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handleEmail}
              size="lg"
              data-testid="hero-email-btn"
              className="bg-white text-[#1A3260] hover:bg-gray-100 text-base md:text-lg px-8 py-6 font-semibold"
            >
              <Mail className="mr-2 h-5 w-5" />
              {t.hero.emailUs}
            </Button>
            <Button 
              onClick={handleWhatsApp}
              size="lg"
              variant="outline"
              data-testid="hero-whatsapp-btn"
              className="border-2 border-white text-white hover:bg-white/10 text-base md:text-lg px-8 py-6 font-semibold"
            >
              <Phone className="mr-2 h-5 w-5" />
              {t.hero.whatsappUs}
            </Button>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" data-testid="about-us-section" className="py-20 px-6 bg-white" style={{ paddingTop: '80px', paddingBottom: '80px' }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-12">
            {t.aboutUs.title}
          </h2>
          <div className="space-y-7" style={{ fontSize: '18px', lineHeight: '1.8' }}>
            <p className="text-gray-600">{t.aboutUs.text}</p>
            <p className="text-gray-600">{t.aboutUs.text2}</p>
            <div className="flex justify-center">
              <p className="border-l-[3px] border-[#1A3260] pl-6 text-left font-medium text-[#1A3260] italic">
                {t.aboutUs.text3}
              </p>
            </div>
            <p className="text-xl font-bold text-[#1A3260] pt-2">
              {t.aboutUs.tagline}
            </p>
          </div>
        </div>
      </section>

      {/* Our Values Section - Navy Blue Background */}
      <section data-testid="values-section" className="py-20 px-6 bg-[#1A3260] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold mb-4">{t.about.valuesTitle}</h2>
            <p className="text-lg text-white/80">{t.values.subtitle}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            <div className="text-center">
              <div className="bg-white/15 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-5">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{t.about.integrityTitle}</h3>
              <p className="text-white/75 leading-relaxed">{t.about.integrityDesc}</p>
            </div>
            <div className="text-center">
              <div className="bg-white/15 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-5">
                <ClipboardCheck className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{t.about.responsibilityTitle}</h3>
              <p className="text-white/75 leading-relaxed">{t.about.responsibilityDesc}</p>
            </div>
            <div className="text-center">
              <div className="bg-white/15 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-5">
                <Handshake className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{t.about.partnershipTitle}</h3>
              <p className="text-white/75 leading-relaxed">{t.about.partnershipDesc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" data-testid="services-section" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t.services.title}
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
              {t.services.subtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} data-testid={`service-card-${index}`} className="border border-gray-200 border-l-[3px] border-l-[#1A3260] hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8">
                  <service.icon className="h-12 w-12 text-[#1A3260] mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What You Gain Section */}
      <section data-testid="benefits-section" className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t.benefits.title}
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
              {t.benefits.subtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((item, index) => (
              <div key={index} data-testid={`benefit-card-${index}`} className="text-center">
                <div className="bg-[#1A3260] rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" data-testid="process-section" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t.process.title}
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
              {t.process.subtitle}
            </p>
          </div>
          <div className="relative">
            <div className="grid md:grid-cols-5 gap-6">
              {processSteps.map((step, index) => (
                <div key={index} data-testid={`process-step-${index}`} className="text-center">
                  <div className="bg-[#1A3260] text-white rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4 text-2xl font-bold shadow-lg">
                    {step.number}
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" data-testid="cta-section" className="py-20 px-6 bg-[#1A3260]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t.cta.title}
          </h2>
          <p className="text-base md:text-lg text-white/85 mb-10 leading-relaxed">
            {t.cta.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handleEmail}
              size="lg"
              data-testid="cta-email-btn"
              className="bg-white text-[#1A3260] hover:bg-gray-100 text-base md:text-lg px-10 py-6 font-semibold"
            >
              <Mail className="mr-2 h-5 w-5" />
              {t.hero.emailUs}
            </Button>
            <Button 
              onClick={handleWhatsApp}
              size="lg"
              variant="outline"
              data-testid="cta-whatsapp-btn"
              className="border-2 border-white text-white hover:bg-white/10 text-base md:text-lg px-10 py-6 font-semibold"
            >
              <Phone className="mr-2 h-5 w-5" />
              {t.hero.whatsappUs}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer data-testid="site-footer" className="bg-gray-900 text-white py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12">
            <div>
              <div className="mb-4">
                <h3 className="text-2xl font-bold text-white">NexumTrades</h3>
              </div>
              <p className="text-gray-400 leading-relaxed">
                {t.footer.description}
              </p>
              <p className="text-blue-400 font-semibold mt-4">
                {t.hero.tagline}
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">{t.footer.contact}</h3>
              <div className="space-y-3 text-gray-400">
                <p className="flex items-center">
                  <Mail className="h-5 w-5 mr-2 flex-shrink-0" />
                  info@nexumtrades.com
                </p>
                <p className="flex items-center">
                  <Phone className="h-5 w-5 mr-2 flex-shrink-0" />
                  +351 927 130 90
                </p>
                <p className="flex items-center">
                  <ExternalLink className="h-5 w-5 mr-2 flex-shrink-0" />
                  {t.footer.website}
                </p>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">{t.footer.quickLinks}</h3>
              <div className="space-y-2">
                <a href="#about" className="block text-gray-400 hover:text-white transition-colors">{t.footer.aboutUs}</a>
                <a href="#services" className="block text-gray-400 hover:text-white transition-colors">{t.nav.services}</a>
                <a href="#process" className="block text-gray-400 hover:text-white transition-colors">{t.footer.howWeWork}</a>
                <a href="#contact" className="block text-gray-400 hover:text-white transition-colors">{t.nav.contact}</a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} NexumTrades. {t.footer.rights}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
 Home;
